// Relative on purpose: vite.config.ts loads this file at build time (utilities/pageMetaPrerenderPlugin.ts), where the
// `@/` alias does not resolve. Keep its imports free of browser code.
import { ROUTES } from "../router/routes"
import { EASETECH } from "../utilities/global-variables"

export const SITE_NAME = "Easetech"
export const SITE_ORIGIN = "https://easetech.co.za"
const PRODUCTION_HOSTS = ["easetech.co.za", "www.easetech.co.za"]

export const DEFAULT_DESCRIPTION =
  "Easetech is a wayleave management platform for South African infrastructure projects — submit, track and approve wayleave applications, and get expert wayleave procurement consulting."

export type PageMeta = {
  title: string
  description: string
  /** Absolute canonical URL; null when the page should not declare one. */
  canonical: string | null
  indexable: boolean
}

type PageCopy = { title: string; description: string }

const { website } = ROUTES

// Keyed by the path segment after `/:org/`. Only public marketing pages are listed; anything
// else under `/:org/` (auth, dashboard, unknown) falls through to noindex.
const ORG_PAGES: Record<string, PageCopy> = {
  [website.home]: {
    title: "Wayleave Management Software & Consulting",
    description: DEFAULT_DESCRIPTION,
  },
  // "wayleave management system" is the wording people search for (Search Console); the page's h1 matches it.
  [website.software]: {
    title: "Wayleave Management System for Municipalities & Utilities",
    description:
      "Wayleave management system for South African municipalities and utilities: online applications, GIS checks of affected services, tracked deadlines and faster approvals.",
  },
  [website.aoi]: {
    title: "Area of Interest (AOI) Applications",
    description:
      "Submit Area of Interest applications online, check affected infrastructure on the map, and track every stakeholder response in one place.",
  },
  [website.ourServices]: {
    title: "Our Services",
    description:
      "Wayleave application management, processing management and procurement services for municipalities, utilities, road, rail and private infrastructure owners.",
  },
  [website.consulting]: {
    title: "Wayleave Consulting & Procurement",
    description:
      "Expert wayleave procurement for municipal, national and provincial road, railway and third-party approvals — faster turnaround, full legal compliance.",
  },
  [website.channelPartners]: {
    title: "One Submission for Every Wayleave",
    description:
      "Submit once and reach every affected infrastructure owner. Track all approvals in one place and reduce the risk of a missed approval.",
  },
  [website.quote]: {
    title: "Get a Free Wayleave Quote",
    description:
      "Draw your project on the map or upload a KML file and receive a detailed, obligation-free wayleave quote within 24 hours.",
  },
}

// Top-level (org-less) public pages.
const ROOT_PAGES: Record<string, PageCopy> = {
  [website.privacyPolicy]: {
    title: "Privacy Policy",
    description: "How Easetech collects, uses and protects your personal information.",
  },
  [website.termsAndConditions]: {
    title: "Terms and Conditions",
    description: "The terms that govern use of the Easetech wayleave management platform.",
  },
  [website.preparingGeometry]: {
    title: "Preparing Project Geometry",
    description:
      "How to prepare KML and map geometry for your wayleave or AOI application so affected infrastructure is detected accurately.",
  },
}

export const formatTitle = (title: string): string => `${title} | ${SITE_NAME}`

const noIndex = (title: string): PageMeta => ({
  title: formatTitle(title),
  description: DEFAULT_DESCRIPTION,
  canonical: null,
  indexable: false,
})

export const isProductionHost = (hostname: string): boolean => PRODUCTION_HOSTS.includes(hostname.toLowerCase())

/** Resolves title/description/canonical/robots for a pathname. Pure — no DOM access. */
export const getPageMeta = (pathname: string): PageMeta => {
  const segments = pathname.split("/").filter(Boolean)

  // `/` and `/easetech/home` render the same page — `/` is canonical.
  if (segments.length === 0) {
    return {
      ...toMeta(ORG_PAGES[website.home], "/"),
      title: `${SITE_NAME} | Wayleave Management Software & Consulting`,
    }
  }

  if (segments.length === 1 && ROOT_PAGES[segments[0]]) {
    return toMeta(ROOT_PAGES[segments[0]], `/${segments[0]}`)
  }

  const [org, page, ...rest] = segments
  if (segments[0] === "dashboard" || page === website.auth || page === "dashboard") return noIndex("Platform")
  if (page === website.thankYouPage) return noIndex("Thank You")
  // The blog only says "Coming soon": a near-empty page in search. List it in ORG_PAGES and the sitemap with its
  // first posts.
  if (page === website.blog) return noIndex("Blog")

  const copy = page && rest.length === 0 ? ORG_PAGES[page] : undefined
  if (!copy) return noIndex("Page Not Found")

  const slug = org.toLowerCase()
  if (slug === EASETECH && page === website.home) return getPageMeta("/")

  return toMeta(copy, `/${slug}/${page}`)
}

const toMeta = (copy: PageCopy, path: string): PageMeta => ({
  title: formatTitle(copy.title),
  description: copy.description,
  canonical: `${SITE_ORIGIN}${path}`,
  indexable: true,
})
